import { useEffect, useRef, useState } from "react";
import { FileText, Loader2, X } from "lucide-react";
import { ApiError } from "@/lib/api/client";
import { isImageMimeType, validateAttachment, type JobAttachment } from "@/lib/api/mailbox";
import { cn } from "@/lib/utils";
import { collectUploadedIds, documentLabel, type TrayItem } from "@/pages/mailbox/job-detail";

/**
 * Files in the idea chat (ER-133): the files waiting to go with the next
 * message, and the files already sent with a message. Same rules as the
 * conversation page's tray: images, PDF, text, CSV, Markdown and JSON up to
 * 2 MB, checked here for quick feedback and again by the API.
 */

/** Staged files for the next message, each uploading on its own. */
export function useAttachmentTray(upload: ((file: File) => Promise<JobAttachment>) | undefined) {
  const [tray, setTray] = useState<TrayItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const counter = useRef(0);
  // Mirrors the tray so leaving the page frees every preview.
  const trayRef = useRef<TrayItem[]>([]);
  trayRef.current = tray;
  useEffect(
    () => () => {
      for (const item of trayRef.current)
        if (item.isObjectUrl) URL.revokeObjectURL(item.previewUrl);
    },
    []
  );

  const add = (files: readonly File[]) => {
    if (!upload) return;
    for (const file of files) {
      const invalid = validateAttachment(file);
      if (invalid) {
        setError(`${file.name}: ${invalid}`);
        continue;
      }
      setError(null);
      const key = `att-${String(counter.current++)}`;
      const isImage = file.type.startsWith("image/");
      const previewUrl = isImage ? URL.createObjectURL(file) : "";
      setTray((prev) => [
        ...prev,
        {
          key,
          status: "uploading",
          previewUrl,
          isObjectUrl: isImage,
          name: file.name || "file",
          isImage,
        },
      ]);
      upload(file).then(
        (attachment) => {
          setTray((prev) =>
            prev.map((i) =>
              i.key === key ? { ...i, status: "done", attachmentId: attachment.id } : i
            )
          );
        },
        (e: unknown) => {
          const message = e instanceof ApiError ? e.message : "Upload failed";
          setTray((prev) =>
            prev.map((i) => (i.key === key ? { ...i, status: "error", error: message } : i))
          );
        }
      );
    }
  };

  const remove = (key: string) => {
    setTray((prev) => {
      const item = prev.find((i) => i.key === key);
      if (item?.isObjectUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((i) => i.key !== key);
    });
  };

  /** Empties the tray after a message went out with its files. */
  const clear = () => {
    setTray((prev) => {
      for (const item of prev) if (item.isObjectUrl) URL.revokeObjectURL(item.previewUrl);
      return [];
    });
  };

  return {
    tray,
    error,
    add,
    remove,
    clear,
    uploadedIds: collectUploadedIds(tray),
    uploading: tray.some((i) => i.status === "uploading"),
  };
}

/** The files waiting to go with the next message. */
export function AttachmentTray({
  tray,
  onRemove,
  terminal,
}: {
  tray: readonly TrayItem[];
  onRemove: (key: string) => void;
  terminal: boolean;
}) {
  if (tray.length === 0) return null;
  return (
    <ul aria-label="Files to send" className="flex flex-wrap gap-2">
      {tray.map((item) => (
        <li
          key={item.key}
          className={cn(
            "flex min-h-9 max-w-full items-center gap-2 rounded-lg border px-2 py-1 text-xs",
            terminal
              ? "border-slate-700 text-slate-300"
              : "border-border bg-background text-foreground",
            item.status === "error" && (terminal ? "border-red-500/60" : "border-destructive")
          )}
        >
          {item.isImage && item.previewUrl ? (
            <img src={item.previewUrl} alt="" className="h-7 w-7 shrink-0 rounded object-cover" />
          ) : (
            <FileText aria-hidden="true" className="h-4 w-4 shrink-0" />
          )}
          <span className="min-w-0 truncate" title={item.name}>
            {item.name}
          </span>
          {item.status === "uploading" && (
            <Loader2 aria-label="Uploading" className="h-3.5 w-3.5 shrink-0 animate-spin" />
          )}
          {item.status === "done" && (
            <span aria-label="Uploaded" className="shrink-0 text-green-500">
              ✓
            </span>
          )}
          {item.status === "error" && (
            <span
              role="alert"
              className={cn("shrink-0", terminal ? "text-red-300" : "text-destructive")}
            >
              {item.error}
            </span>
          )}
          <button
            type="button"
            aria-label={`Remove ${item.name}`}
            onClick={() => {
              onRemove(item.key);
            }}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-black/10 dark:hover:bg-white/10"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Files sent with a message, under its text. */
export function SentAttachments({
  attachments,
  terminal,
  mine,
}: {
  attachments: readonly JobAttachment[] | undefined;
  terminal: boolean;
  mine?: boolean;
}) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <span className={cn("mt-1.5 flex flex-wrap gap-2", mine && "justify-end")}>
      {attachments.map((a) =>
        isImageMimeType(a.mimeType) ? (
          <a
            key={a.id}
            href={a.signedUrl}
            target="_blank"
            rel="noreferrer"
            className="block overflow-hidden rounded-md border border-black/10 dark:border-white/15"
          >
            <img
              src={a.signedUrl}
              alt="Attached image"
              loading="lazy"
              className="h-20 w-20 object-cover"
            />
          </a>
        ) : (
          <a
            key={a.id}
            href={a.signedUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`Attached file (${documentLabel(a.mimeType)})`}
            className={cn(
              "inline-flex min-h-9 items-center gap-1.5 rounded-md border px-2.5 text-xs underline-offset-2 hover:underline",
              terminal ? "border-slate-700 text-slate-300" : "border-black/15 dark:border-white/20"
            )}
          >
            <FileText aria-hidden="true" className="h-4 w-4" />
            {documentLabel(a.mimeType)}
          </a>
        )
      )}
    </span>
  );
}
